import { getTagType } from '@/shared/utils/getTagType.ts';
import { PRODUCT_GROUP_TYPE } from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { useBalanceSheetCalculations } from '@/shared/components/ProductGroupHeader/hooks/useBalanceSheetCalculations.ts';
import { LabeledList, Tag } from '@schuettflix/react-components';
import { BalanceProductPropsWithFallbackValues } from './context/BalanceSheetContext';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';

export type BalanceSheetProps = {
    products: BalanceProductPropsWithFallbackValues[];
    productGroupType: PRODUCT_GROUP_TYPE;
};

export function BalanceSheet({ products, productGroupType }: BalanceSheetProps) {
    const { t } = useTranslation();
    const {
        customerPrice,
        customerPriceString,
        materialPrice,
        materialPriceString,
        transportPriceString,
        servicePrice,
        servicePriceString,
        transportPrice,
        takeRate,
        takeRateString,
    } = useBalanceSheetCalculations({
        products,
    });

    return (
        <LabeledList size="md">
            <LabeledList.Row label={t('product.balanceSheet.client')}>
                <Tag
                    label={customerPriceString}
                    subdued
                    type={getTagType({ value: customerPrice })}
                    data-test="customer"
                />
            </LabeledList.Row>
            {materialPrice !== null && (
                <LabeledList.Row
                    label={
                        productGroupType === 'MERCHANT'
                            ? t('product.category.label.material_transport')
                            : t(PRODUCT_CATEGORY_LABEL['MATERIAL'])
                    }
                >
                    <Tag
                        label={materialPriceString}
                        subdued
                        type={getTagType({ value: materialPrice, reverseFlag: true })}
                        data-test="material"
                    />
                </LabeledList.Row>
            )}
            {transportPrice !== null && productGroupType !== 'MERCHANT' && (
                <LabeledList.Row label={t(PRODUCT_CATEGORY_LABEL['TRANSPORT'])}>
                    <Tag
                        label={transportPriceString}
                        subdued
                        type={getTagType({ value: transportPrice, reverseFlag: true })}
                        data-test="transport"
                    />
                </LabeledList.Row>
            )}
            {servicePrice !== null && (
                <LabeledList.Row label={t(PRODUCT_CATEGORY_LABEL['MATERIAL_SERVICE'])}>
                    <Tag
                        label={servicePriceString}
                        subdued
                        type={getTagType({ value: servicePrice, reverseFlag: true })}
                        data-test="service"
                    />
                </LabeledList.Row>
            )}
            <LabeledList.Row label={t('product.balanceSheet.takeRate')}>
                <Tag
                    label={takeRateString ?? '---'}
                    subdued
                    type={getTagType({ value: takeRate ?? 0 })}
                    data-test="take-rate"
                />
            </LabeledList.Row>
        </LabeledList>
    );
}
